import { useState } from 'react';

import { Button } from '../../button';
import { Input } from '../../input';

import {
  connectionTransition,
  databaseTasks,
  historicalTasks,
  identityTransition,
  initialConnection,
  initialIdentity,
  initialTransaction,
  inspectDatabaseRow,
  parameterFrame,
  queryRows,
  queryTemplate,
  schemaCandidate,
  scopeChange,
  transactionTransition,
  writersFrame,
  type DatabaseTask,
  type ImportCounterRule,
  type ParameterDesign,
  type QueryFilter,
  type QueryOrder,
  type QueryProjection,
  type SchemaExample,
  type ScopeOperation,
  type ScopeTarget,
  type WriterDesign,
} from './database-models';
import { Lab, Panel, Reset, Result } from './lab-parts';
import { ModelSelect } from './learning-model-surface';

import './database-labs.css';

function Predict({ children }: { children: string }) {
  return (
    <p className="db-predict">
      <strong>Predict first</strong> {children}
    </p>
  );
}

function Rows({
  rows,
  label,
  titlesOnly = false,
}: {
  rows: DatabaseTask[];
  label: string;
  titlesOnly?: boolean;
}) {
  return (
    <div role="group" aria-label={label}>
      <div
        className="db-rows cb-record-grid"
        role="table"
        aria-label={label}
        data-titles-only={titlesOnly}
      >
        <div role="row" className="db-row db-row-heading">
          {!titlesOnly && <span role="columnheader">id</span>}
          <span role="columnheader">title</span>
          {!titlesOnly && <span role="columnheader">completed</span>}
        </div>
        {rows.map((row) => (
          <div role="row" className="db-row" key={row.id}>
            {!titlesOnly && <span role="cell">{row.id}</span>}
            <span role="cell">{row.title}</span>
            {!titlesOnly && <span role="cell">{String(row.completed)}</span>}
          </div>
        ))}
        {rows.length === 0 && (
          <div role="row" className="db-empty">
            <span role="cell">No rows</span>
          </div>
        )}
      </div>
    </div>
  );
}

function Facts({ values }: { values: [string, string | number][] }) {
  return (
    <dl className="cb-memory">
      {values.map(([label, value]) => (
        <div key={label}>
          <dt>{label}</dt>
          <dd>{value}</dd>
        </div>
      ))}
    </dl>
  );
}

export function DatabaseWritersLab() {
  const [design, setDesign] = useState<WriterDesign>('copies');
  const [step, setStep] = useState(0);
  const frame = writersFrame(step, design);
  const coordinated = design === 'coordinator';
  const stages = [
    'Start together',
    'A reads',
    coordinated ? 'B waits' : 'B reads',
    'A saves completion',
    'B saves title',
  ];
  const messages = [
    'Two application processes share one saved collection. A will complete task #1; B will rename task #2.',
    'A reads a private copy. Its planned completion is not saved yet.',
    coordinated
      ? 'The shared coordinator makes B wait before reading. It protects the whole read/change/write operation.'
      : design === 'local-locks'
        ? 'Each process owns a different lock. Both can hold their own lock and read the same old state.'
        : 'B reads the same old collection before A saves. Their private copies now overlap.',
    'A completes task #1 and saves. B has not saved its title change yet.',
    coordinated
      ? 'B reads after A saves, then renames task #2 and saves. Both changes remain.'
      : 'B saves its old copy with a new title. Task #1 becomes open again: A’s completion was lost.',
  ];
  return (
    <Lab
      title="Two writers, one saved collection"
      eyebrow="Database model · coordination"
      className="db-lab"
    >
      <Predict>
        Let both writers read before either saves. Will changing different tasks protect both
        changes?
      </Predict>
      <div className="cb-controls">
        <label>
          Coordination rule
          <ModelSelect
            aria-label="Coordination rule"
            value={design}
            onValueChange={(value) => {
              setDesign(value as WriterDesign);
              setStep(0);
            }}
            options={[
              { value: 'copies', label: 'Independent file copies' },
              { value: 'local-locks', label: 'A separate lock in each process' },
              { value: 'coordinator', label: 'One shared coordinator' },
            ]}
          />
        </label>
      </div>
      <ol className="db-steps" aria-label="Writer progress">
        {stages.map((stage, index) => (
          <li key={stage} aria-current={index === step ? 'step' : undefined}>
            <span>{index + 1}</span>
            {stage}
          </li>
        ))}
      </ol>
      <div className="db-three" data-design={design}>
        <Panel label="A · private working copy">
          {frame.a ? <Rows rows={frame.a} label="Writer A copy" /> : <p>Not read yet</p>}
        </Panel>
        <Panel label="B · private working copy">
          {frame.b ? (
            <Rows rows={frame.b} label="Writer B copy" />
          ) : (
            <p>{coordinated && step >= 2 ? 'Waiting for A to finish' : 'Not read yet'}</p>
          )}
        </Panel>
        <Panel label="Shared saved collection">
          <Rows rows={frame.stored} label="Shared saved rows" />
        </Panel>
      </div>
      <div className="cb-controls">
        <Button size="sm" disabled={step === 4} onClick={() => setStep(step + 1)}>
          Advance writers
        </Button>
        <Reset
          onClick={() => {
            setDesign('copies');
            setStep(0);
          }}
        />
      </div>
      <Result tone={frame.lostUpdate ? 'warning' : step === 4 ? 'success' : 'neutral'}>
        {messages[step]}
      </Result>
      <p className="db-caption">
        The refinement coordinates the entire read/change/write operation. Moving data into a
        database alone does not make arbitrary application reads and later writes safe from lost
        updates.
      </p>
    </Lab>
  );
}

export function DatabaseQueryLab() {
  const [filter, setFilter] = useState<QueryFilter>('open');
  const [projection, setProjection] = useState<QueryProjection>('rows');
  const [order, setOrder] = useState<QueryOrder>('asc');
  const [rows, setRows] = useState(databaseTasks);
  const [queried, setQueried] = useState(false);
  const result = queryRows(rows, filter, order);
  return (
    <Lab
      title="Ask for the rows and columns you need"
      eyebrow="Database model · query results"
      className="db-lab"
    >
      <Predict>
        If the result contains only open-task titles, have completed tasks been deleted from
        storage?
      </Predict>
      <div className="cb-controls">
        <label>
          Which rows?
          <ModelSelect
            aria-label="Query filter"
            value={filter}
            onValueChange={(value) => {
              setFilter(value as QueryFilter);
              setQueried(false);
            }}
            options={[
              { value: 'all', label: 'All tasks' },
              { value: 'open', label: 'Open tasks' },
              { value: 'complete', label: 'Completed tasks' },
            ]}
          />
        </label>
        <label>
          Which columns?
          <ModelSelect
            aria-label="Query columns"
            value={projection}
            onValueChange={(value) => {
              setProjection(value as QueryProjection);
              setQueried(false);
            }}
            options={[
              { value: 'rows', label: 'id, title, completed' },
              { value: 'titles', label: 'title only' },
            ]}
          />
        </label>
        <label>
          Result order
          <ModelSelect
            aria-label="Query order"
            value={order}
            onValueChange={(value) => {
              setOrder(value as QueryOrder);
              setQueried(false);
            }}
            options={[
              { value: 'asc', label: 'ID ascending' },
              { value: 'desc', label: 'ID descending' },
            ]}
          />
        </label>
      </div>
      <pre aria-label="Query SQL">{queryTemplate(filter, projection, order)}</pre>
      <div className="cb-two">
        <Panel label="Stored collection">
          <Rows rows={rows} label="Stored query rows" />
          <p className="db-caption">
            A whole-file reader loads all {rows.length} records before application filtering.
          </p>
        </Panel>
        <Panel label="Query result returned to the application">
          {queried ? (
            <Rows rows={result} label="Query result" titlesOnly={projection === 'titles'} />
          ) : (
            <p>Apply the query to see its result.</p>
          )}
        </Panel>
      </div>
      <div className="cb-controls">
        <Button size="sm" onClick={() => setQueried(true)}>
          Apply query
        </Button>
        <Button
          size="sm"
          variant="secondary"
          disabled={rows.every((row) => row.completed)}
          onClick={() => {
            setRows(rows.map((row) => ({ ...row, completed: true })));
            setQueried(false);
          }}
        >
          Complete all stored tasks
        </Button>
        <Reset
          onClick={() => {
            setFilter('open');
            setProjection('rows');
            setOrder('asc');
            setRows(databaseTasks());
            setQueried(false);
          }}
        />
      </div>
      <Result>
        {queried
          ? `${result.length} of ${rows.length} stored rows returned, ${projection === 'titles' ? 'with title only' : 'with all three columns'}. SELECT did not change storage.${result.length === 0 ? ' An empty result is a successful query.' : ''}`
          : 'Change the filter, selected columns or sort order, then apply the query. Mark every stored task complete and check what the open-task filter returns.'}
      </Result>
      <p className="db-caption">
        Filtering controls the result sent to the application. It does not by itself promise that
        the database inspects fewer rows; indexes and query plans come later.
      </p>
    </Lab>
  );
}

export function DatabaseSchemaLab() {
  const [example, setExample] = useState<SchemaExample>('valid');
  const [attempted, setAttempted] = useState(false);
  const initial = databaseTasks().slice(0, 1);
  const candidate = schemaCandidate(example);
  const inspection = inspectDatabaseRow(initial, candidate);
  const rows = attempted && inspection.accepted ? [...initial, inspection.row] : initial;
  return (
    <Lab
      title="A record must fit the table’s rules"
      eyebrow="Database model · columns and constraints"
      className="db-lab"
    >
      <Predict>
        A record is valid JSON. Does that guarantee its ID, title and completion value fit this
        table?
      </Predict>
      <div className="cb-controls">
        <label>
          New record
          <ModelSelect
            aria-label="New record"
            value={example}
            onValueChange={(value) => {
              setExample(value as SchemaExample);
              setAttempted(false);
            }}
            options={[
              { value: 'valid', label: 'Valid typed row' },
              { value: 'duplicate', label: 'An existing primary key' },
              { value: 'boolean', label: 'Boolean input “perhaps”' },
              { value: 'blank', label: 'A blank title' },
              { value: 'nul', label: 'A title containing NUL' },
            ]}
          />
        </label>
      </div>
      <div className="cb-two">
        <Panel label="Proposed record">
          <pre>{JSON.stringify(candidate, null, 2)}</pre>
          <Facts
            values={[
              ['id', 'BIGINT · identity · primary key'],
              ['title', 'TEXT · required · trimmed · 1–80 characters'],
              ['completed', 'BOOLEAN · required'],
            ]}
          />
        </Panel>
        <Panel label="Accepted table rows">
          <Rows rows={rows} label="Schema table" />
        </Panel>
      </div>
      <div className="cb-controls">
        <Button size="sm" disabled={attempted} onClick={() => setAttempted(true)}>
          Try inserting record
        </Button>
        <Reset
          onClick={() => {
            setExample('valid');
            setAttempted(false);
          }}
        />
      </div>
      <Result tone={attempted ? (inspection.accepted ? 'success' : 'warning') : 'neutral'}>
        {attempted
          ? `${inspection.reason}${inspection.accepted ? '' : ' The accepted rows stayed unchanged.'}`
          : 'Inspect the new record, then try inserting it. Changing examples restores the original table.'}
      </Result>
      <p className="db-caption">
        SQL can convert recognized inputs such as the text literal 'false' into a boolean. “perhaps”
        has no boolean meaning. Strict API input validation and database type conversion are
        separate boundaries.
      </p>
    </Lab>
  );
}

export function DatabaseConnectionLab() {
  const [state, setState] = useState(initialConnection);
  const act = (action: Parameters<typeof connectionTransition>[1]) =>
    setState((current) => connectionTransition(current, action));
  return (
    <Lab
      title="Process, connection and transaction have different lifetimes"
      eyebrow="Database model · boundaries"
      className="db-lab"
    >
      <Predict>
        Commit a change, then stop the application. Which state should remain when it starts again?
      </Predict>
      <div className="db-three">
        <Panel label="Application process">
          <Facts
            values={[
              ['Application', state.app ? 'Running' : 'Stopped'],
              ['Connection', state.connected ? 'Open' : 'Closed'],
              ['Transaction', state.pending ? 'Active · pending work' : 'None'],
            ]}
          />
        </Panel>
        <Panel label="Working transaction">
          {state.pending ? (
            <Rows rows={state.pending} label="Connection pending rows" />
          ) : (
            <p>No pending transaction</p>
          )}
        </Panel>
        <Panel label="Database and durable storage">
          <Facts values={[['Database process', state.database ? 'Running' : 'Stopped']]} />
          <Rows rows={state.stored} label="Connection committed rows" />
        </Panel>
      </div>
      <div className="cb-controls">
        <Button
          size="sm"
          disabled={!state.app || !state.database || state.connected}
          onClick={() => act('connect')}
        >
          Open connection
        </Button>
        <Button
          size="sm"
          disabled={!state.connected || Boolean(state.pending)}
          onClick={() => act('begin')}
        >
          Begin transaction
        </Button>
        <Button
          size="sm"
          disabled={!state.pending || state.pending[0]?.completed}
          onClick={() => act('change')}
        >
          Complete task #1
        </Button>
        <Button size="sm" disabled={!state.pending} onClick={() => act('commit')}>
          Commit transaction
        </Button>
        <Button
          size="sm"
          variant="secondary"
          disabled={!state.connected}
          onClick={() => act('disconnect')}
        >
          Close connection
        </Button>
      </div>
      <div className="cb-controls">
        <Button
          size="sm"
          variant="secondary"
          onClick={() => act(state.app ? 'stop-app' : 'start-app')}
        >
          {state.app ? 'Stop application' : 'Start application'}
        </Button>
        <Button
          size="sm"
          variant="secondary"
          onClick={() => act(state.database ? 'stop-database' : 'start-database')}
        >
          {state.database ? 'Stop database process' : 'Start database process'}
        </Button>
        <Reset onClick={() => act('reset')} />
      </div>
      <Result>{state.message}</Result>
      <p className="db-caption">
        This timeline uses explicit BEGIN. A Python driver can start transactions automatically.
        Closing a connection with pending work rolls that work back; closing it after commit does
        not undo the commit.
      </p>
    </Lab>
  );
}

export function DatabaseScopeLab() {
  const [operation, setOperation] = useState<ScopeOperation>('update');
  const [target, setTarget] = useState<ScopeTarget>('1');
  const [applied, setApplied] = useState(false);
  const before = databaseTasks();
  const change = scopeChange(before, operation, target);
  return (
    <Lab
      title="Which rows will this statement change?"
      eyebrow="Database model · WHERE selects rows to change"
      className="db-lab"
    >
      <Predict>Remove the WHERE clause. Will the database infer the one task you meant?</Predict>
      <div className="cb-controls">
        <label>
          Operation
          <ModelSelect
            aria-label="SQL operation"
            value={operation}
            onValueChange={(value) => {
              setOperation(value as ScopeOperation);
              setApplied(false);
            }}
            options={[
              { value: 'update', label: 'UPDATE completed = TRUE' },
              { value: 'delete', label: 'DELETE rows' },
            ]}
          />
        </label>
        <label>
          Row condition
          <ModelSelect
            aria-label="Row condition"
            value={target}
            onValueChange={(value) => {
              setTarget(value as ScopeTarget);
              setApplied(false);
            }}
            options={[
              { value: '1', label: 'WHERE id = 1' },
              { value: '99', label: 'WHERE id = 99 · missing' },
              { value: 'all', label: 'No WHERE clause' },
            ]}
          />
        </label>
      </div>
      <pre>{change.sql}</pre>
      <Facts
        values={[
          ['Matching IDs', change.matchedIds.length ? change.matchedIds.join(', ') : 'None'],
          ['Rows matched', change.matchedIds.length],
        ]}
      />
      <div className="cb-two">
        <Panel label="Before statement">
          <Rows rows={before} label="Rows before" />
        </Panel>
        <Panel label="After statement">
          {applied ? (
            <Rows rows={change.after} label="Rows after" />
          ) : (
            <p>Predict the result, then apply the statement.</p>
          )}
        </Panel>
      </div>
      <div className="cb-controls">
        <Button size="sm" disabled={applied} onClick={() => setApplied(true)}>
          Apply statement
        </Button>
        <Reset
          onClick={() => {
            setOperation('update');
            setTarget('1');
            setApplied(false);
          }}
        />
      </div>
      <Result tone={applied && target === 'all' ? 'warning' : 'neutral'}>
        {applied
          ? `${change.matchedIds.length} rows matched. ${target === 'all' ? 'Without WHERE, every row was changed.' : target === '99' ? 'No row matched ID 99. The statement succeeded with no returned rows; the API must decide how to report a missing task.' : 'Only task #1 was changed.'}`
          : 'WHERE selects the rows to change. Each changed control restores the same three starting rows.'}
      </Result>
    </Lab>
  );
}

export function DatabaseParametersLab() {
  const [title, setTitle] = useState('Read notes');
  const [design, setDesign] = useState<ParameterDesign>('interpolation');
  const frame = parameterFrame(title, design);
  return (
    <Lab
      title="Keep a title out of SQL syntax"
      eyebrow="Database model · bound values"
      className="db-lab"
    >
      <Predict>
        Change the title to Read Maya’s notes, with a straight apostrophe. What happens to the
        quoted SQL text?
      </Predict>
      <div className="cb-controls">
        <label>
          Task title
          <Input
            aria-label="SQL task title"
            value={title}
            maxLength={160}
            onChange={(event) => setTitle(event.target.value)}
          />
        </label>
        <label>
          Build the statement with
          <ModelSelect
            aria-label="SQL value handling"
            value={design}
            onValueChange={(value) => setDesign(value as ParameterDesign)}
            options={[
              { value: 'interpolation', label: 'Insert title into SQL text' },
              { value: 'bound', label: 'A placeholder and separate value' },
            ]}
          />
        </label>
      </div>
      <div className="cb-two" data-design={design}>
        <Panel label={design === 'bound' ? 'Fixed SQL template' : 'Constructed SQL text'}>
          <pre>{frame.template}</pre>
        </Panel>
        <Panel label="Separate parameter values">
          <pre>{frame.parameters}</pre>
        </Panel>
      </div>
      <div className="cb-controls">
        <Button size="sm" variant="secondary" onClick={() => setTitle("Read Maya's notes")}>
          Try an apostrophe
        </Button>
        <Reset
          onClick={() => {
            setTitle('Read notes');
            setDesign('interpolation');
          }}
        />
      </div>
      <Result tone={design === 'bound' ? 'success' : frame.hasQuote ? 'warning' : 'neutral'}>
        {frame.message}
      </Result>
      <p className="db-caption">
        The %s placeholder is the Python driver’s parameter marker, without quotes around it.
        Binding handles values; title validation and constraints still apply.
      </p>
    </Lab>
  );
}

export function DatabaseTransactionLab() {
  const [state, setState] = useState(initialTransaction);
  const act = (action: Parameters<typeof transactionTransition>[1]) =>
    setState((current) => transactionTransition(current, action));
  const active = state.phase === 'active';
  return (
    <Lab
      title="Two changes, one commit decision"
      eyebrow="Database model · transaction visibility"
      className="db-lab"
    >
      <Predict>
        Change both tasks, then trigger a constraint error. Can the earlier change still be
        committed?
      </Predict>
      <Facts
        values={[
          [
            'Transaction state',
            state.phase === 'aborted'
              ? 'Aborted · rollback required'
              : state.phase === 'active'
                ? 'Active'
                : 'No open transaction',
          ],
        ]}
      />
      <div className="cb-two">
        <Panel label="This transaction · working changes">
          {state.phase === 'aborted' ? (
            <>
              <p>
                Further reads and writes are rejected. These earlier proposed rows will be
                discarded:
              </p>
              <Rows rows={state.pending ?? []} label="Aborted transaction proposals" />
            </>
          ) : state.pending ? (
            <Rows rows={state.pending} label="Transaction working rows" />
          ) : (
            <p>Begin to create a working transaction.</p>
          )}
        </Panel>
        <Panel label="Another connection · new read of committed rows">
          <Rows rows={state.stored} label="Transaction committed rows" />
        </Panel>
      </div>
      <div className="cb-controls">
        <Button size="sm" disabled={state.phase !== 'idle'} onClick={() => act('begin')}>
          Begin transaction
        </Button>
        <Button
          size="sm"
          disabled={!active || state.pending?.[0]?.completed}
          onClick={() => act('complete')}
        >
          Complete task #1
        </Button>
        <Button
          size="sm"
          disabled={!active || state.pending?.[1]?.title === 'Review draft'}
          onClick={() => act('rename')}
        >
          Rename task #4
        </Button>
        <Button size="sm" variant="secondary" disabled={!active} onClick={() => act('error')}>
          Try a blank title
        </Button>
      </div>
      <div className="cb-controls">
        <Button size="sm" disabled={!active} onClick={() => act('commit')}>
          Commit transaction
        </Button>
        <Button
          size="sm"
          variant="secondary"
          disabled={state.phase !== 'aborted'}
          onClick={() => act('complete')}
        >
          Try another statement
        </Button>
        <Button
          size="sm"
          variant="secondary"
          disabled={state.phase === 'idle'}
          onClick={() => act('rollback')}
        >
          Roll back transaction
        </Button>
        <Reset onClick={() => act('reset')} />
      </div>
      <Result tone={state.phase === 'aborted' ? 'warning' : 'neutral'}>{state.message}</Result>
      <p className="db-caption">
        The observer makes a fresh read after each action, using PostgreSQL’s default Read Committed
        behavior. It cannot see another transaction’s uncommitted changes.
      </p>
    </Lab>
  );
}

export function DatabaseIdentityLab() {
  const [state, setState] = useState(initialIdentity);
  const [rule, setRule] = useState<ImportCounterRule>('survivors');
  const act = (action: Parameters<typeof identityTransition>[1]) =>
    setState((current) => identityTransition(current, action, rule));
  const pristine = state.rows.length === 0 && state.nextId === 1;
  return (
    <Lab
      title="Deleted tasks still affect the next ID"
      eyebrow="Database model · generated identity"
      className="db-lab"
    >
      <Predict>
        IDs 5–7 were deleted before import. Will MAX(id) + 1 preserve the promise not to reuse them?
      </Predict>
      <div className="cb-controls">
        <label>
          Import counter rule
          <ModelSelect
            aria-label="Import counter rule"
            value={rule}
            onValueChange={(value) => {
              setRule(value as ImportCounterRule);
              setState(initialIdentity());
            }}
            options={[
              { value: 'survivors', label: 'Highest remaining ID + 1' },
              { value: 'historical', label: 'Preserve JSON next_id' },
            ]}
          />
        </label>
      </div>
      <div className="cb-two">
        <Panel label="Historical JSON snapshot">
          <pre>{JSON.stringify({ next_id: 8, tasks: historicalTasks() }, null, 2)}</pre>
          <p className="db-caption">
            IDs 5–7 were issued and deleted. Their history is in next_id, not the remaining rows.
          </p>
        </Panel>
        <Panel label="Database table and identity sequence">
          <Facts
            values={[
              ['Next generated ID', state.nextId],
              [
                'IDs generated since reset/import',
                state.issued.length ? state.issued.join(', ') : 'None',
              ],
            ]}
          />
          <Rows rows={state.rows} label="Identity table" />
        </Panel>
      </div>
      <div className="cb-controls">
        <Button size="sm" onClick={() => act('insert')}>
          Insert with generated ID
        </Button>
        <Button
          size="sm"
          variant="secondary"
          disabled={state.rows.length === 0}
          onClick={() => act('delete')}
        >
          Delete newest row
        </Button>
        <Button size="sm" variant="secondary" onClick={() => act('rollback')}>
          Generate ID, then roll back
        </Button>
        <Button size="sm" variant="secondary" disabled={!pristine} onClick={() => act('import')}>
          Import historical JSON
        </Button>
        <Reset
          onClick={() => {
            setRule('survivors');
            setState(initialIdentity());
          }}
        />
      </div>
      <Result>{state.message}</Result>
      <p className="db-caption">
        Changing the import rule resets the table to empty and the sequence to unused. Preserving
        next_id commits the imported rows and ALTER COLUMN id RESTART WITH 8 together. When an
        ordinary insert generates an ID, rollback can leave a gap. IDs identify rows; they do not
        count rows.
      </p>
    </Lab>
  );
}
